import { GRADES, base, getLevel } from './levels.js';
import { RULES } from './rules.js';
import type { Celebration, Grade, LevelConfig, MultipleDisplay, RuleId } from './types.js';

/**
 * A teacher's own curriculum.
 *
 * The built-in Grades 1–6 are a starting point, not the structure. A teacher
 * who thinks in terms of "my Tuesday group, four exercises" should not have to
 * translate that into somebody else's year numbering, so they can duplicate the
 * built-in one and then add, rename, reorder and delete freely.
 */

export interface CurriculumLevel {
  id: string;
  /** Teacher's own name for the exercise. Empty falls back to the topic name. */
  name: string;
  config: LevelConfig;
}

export interface CurriculumStage {
  id: string;
  name: string;
  /**
   * Which school year this stage counts as.
   *
   * Badges are earned against a year — "every sub-level of a year cleared" —
   * so a stage has to say which year it stands in for, even when the teacher
   * has renamed it "الأسبوع الثالث".
   */
  grade: Grade;
  levels: CurriculumLevel[];
}

export interface Curriculum {
  id: string;
  name: string;
  stages: CurriculumStage[];
  updatedAt: string;
}

/** What the built-in curriculum is called before a teacher renames a copy. */
export const BUILTIN_ID = 'builtin';

export const MAX_STAGES = 20;
export const MAX_LEVELS_PER_STAGE = 24;
export const MAX_CELLS = 144;
export const MAX_NAME = 60;
/** The multiplier cap's ceiling: the twelve-times table. */
export const MAX_MULTIPLIER = 12;

const RULE_IDS = new Set(Object.keys(RULES) as RuleId[]);
const DISPLAYS = new Set<MultipleDisplay>(['factor', 'division']);
const CELEBRATIONS = new Set<Celebration>(['sparks', 'word', 'full']);

const clampInt = (v: unknown, lo: number, hi: number, fallback: number): number => {
  const n = typeof v === 'number' ? v : Number(v);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(lo, Math.min(hi, Math.round(n)));
};

const bool = (v: unknown, fallback: boolean): boolean =>
  typeof v === 'boolean' ? v : fallback;

const text = (v: unknown, fallback = ''): string =>
  (typeof v === 'string' ? v : fallback).slice(0, MAX_NAME).trim();

/**
 * Clamps a config that came off the wire.
 *
 * The teacher panel is a trusted UI, but the socket and the REST route are not:
 * without this a saved curriculum could ask the server to deal a 400×400 board
 * for thirty players at once. Every field is bounded, and anything unrecognised
 * falls back to the built-in default rather than throwing — a teacher should
 * never lose a whole curriculum to one bad number.
 */
export function sanitizeConfig(raw: unknown): LevelConfig {
  const r = (raw ?? {}) as Partial<LevelConfig>;
  const rule: RuleId = typeof r.rule === 'string' && RULE_IDS.has(r.rule as RuleId)
    ? (r.rule as RuleId)
    : base.rule;

  let rows = clampInt(r.rows, 2, 12, base.rows);
  let cols = clampInt(r.cols, 2, 12, base.cols);
  // Rows are trimmed rather than columns: a board that is too wide stops
  // fitting a phone, while a board that is too tall merely scrolls.
  while (rows * cols > MAX_CELLS && rows > 2) rows--;

  const valueMin = r.valueMin === undefined || r.valueMin === null
    ? undefined : clampInt(r.valueMin, 0, 9999, 1);
  const valueMaxRaw = r.valueMax === undefined || r.valueMax === null
    ? undefined : clampInt(r.valueMax, 1, 9999, 9);
  const valueMax = valueMin !== undefined && valueMaxRaw !== undefined && valueMaxRaw < valueMin
    ? valueMin
    : valueMaxRaw;

  const timerRaw = r.timerSec;
  const timerSec = timerRaw === null || timerRaw === undefined
    ? null
    : clampInt(timerRaw, 10, 1800, 120);

  const density = typeof r.huntDensity === 'number' && Number.isFinite(r.huntDensity)
    ? Math.max(0.15, Math.min(0.8, r.huntDensity))
    : base.huntDensity;

  const puzzle = bool(r.puzzle, base.puzzle);

  return {
    rule,
    ruleTarget: clampInt(r.ruleTarget, 0, 10000, RULES[rule].defaultTarget),
    digits: clampInt(r.digits, 1, 4, base.digits),
    ...(valueMin === undefined ? {} : { valueMin }),
    ...(valueMax === undefined ? {} : { valueMax }),
    rows,
    cols,
    adjacency: bool(r.adjacency, base.adjacency),
    timerSec,
    adds: clampInt(r.adds, 0, 9, base.adds),
    hints: clampInt(r.hints, 0, 9, base.hints),
    missPenalty: clampInt(r.missPenalty, 0, 50, base.missPenalty),
    guaranteeSolvable: bool(r.guaranteeSolvable, base.guaranteeSolvable),
    allowEqualMultiples: bool(r.allowEqualMultiples, base.allowEqualMultiples),
    allowOneAsFactor: bool(r.allowOneAsFactor, base.allowOneAsFactor),
    maxMultiplier: clampInt(r.maxMultiplier, 0, MAX_MULTIPLIER, base.maxMultiplier),
    huntDensity: density,
    puzzle,
    puzzleScene: text(r.puzzleScene),
    multipleDisplay: DISPLAYS.has(r.multipleDisplay as MultipleDisplay)
      ? (r.multipleDisplay as MultipleDisplay) : base.multipleDisplay,
    celebration: CELEBRATIONS.has(r.celebration as Celebration)
      ? (r.celebration as Celebration) : base.celebration,
    decorations: bool(r.decorations, base.decorations),
  };
}

const asGrade = (v: unknown, fallback: Grade = 1): Grade =>
  clampInt(v, 1, 6, fallback) as Grade;

let counter = 0;
/** Ids only have to be unique inside one curriculum, and readable in a log. */
export function newId(prefix: string): string {
  counter = (counter + 1) % 100000;
  return `${prefix}_${Date.now().toString(36)}${counter.toString(36)}`;
}

export function sanitizeCurriculum(raw: unknown, fallbackId = newId('cur')): Curriculum {
  const r = (raw ?? {}) as Partial<Curriculum>;
  const stagesRaw = Array.isArray(r.stages) ? r.stages.slice(0, MAX_STAGES) : [];
  const stages: CurriculumStage[] = stagesRaw.map((s, si) => {
    const st = (s ?? {}) as Partial<CurriculumStage>;
    const levelsRaw = Array.isArray(st.levels) ? st.levels.slice(0, MAX_LEVELS_PER_STAGE) : [];
    return {
      id: text(st.id) || newId('stg'),
      name: text(st.name) || `Stage ${si + 1}`,
      grade: asGrade(st.grade, Math.min(6, si + 1) as Grade),
      levels: levelsRaw.map((l) => {
        const lv = (l ?? {}) as Partial<CurriculumLevel>;
        return {
          id: text(lv.id) || newId('lvl'),
          name: text(lv.name),
          config: sanitizeConfig(lv.config),
        };
      }),
    };
  });

  return {
    id: text(r.id) || fallbackId,
    name: text(r.name) || 'Curriculum',
    stages: stages.length ? stages : builtInCurriculum().stages,
    updatedAt: typeof r.updatedAt === 'string' ? r.updatedAt : new Date().toISOString(),
  };
}

/**
 * The built-in Grades 1–6, expressed as a curriculum.
 *
 * This is what "Duplicate the starter curriculum" hands a teacher, and what a
 * class falls back to when it has none of its own — so the app behaves exactly
 * as it did before anybody opened the editor.
 */
export function builtInCurriculum(): Curriculum {
  return {
    id: BUILTIN_ID,
    name: 'curriculum.builtin',
    updatedAt: new Date(0).toISOString(),
    stages: GRADES.map((g) => ({
      id: `stg_g${g.grade}`,
      name: `msg.grade:${g.grade}`,
      grade: g.grade,
      levels: g.levels.map((l) => ({
        id: `lvl_g${g.grade}_${l.index}`,
        name: `key:${l.nameKey}`,
        config: { ...l.config },
      })),
    })),
  };
}

/** Locates a level inside a curriculum by its stage and level ids. */
export function findCurriculumLevel(
  cur: Curriculum, stageId: string, levelId: string,
): { stage: CurriculumStage; level: CurriculumLevel } | null {
  const stage = cur.stages.find((s) => s.id === stageId);
  const level = stage?.levels.find((l) => l.id === levelId);
  return stage && level ? { stage, level } : null;
}

/**
 * The config a round should actually run.
 *
 * A round names a curriculum level when one was chosen and a grade/level pair
 * otherwise, so a session assembled from the built-in catalogue and one
 * assembled in the editor both resolve through here.
 */
export function configForRound(
  cur: Curriculum | null, stageId: string | null, levelId: string | null,
  grade: Grade, level: number, overrides?: Partial<LevelConfig>,
): LevelConfig {
  if (cur && stageId && levelId) {
    const hit = findCurriculumLevel(cur, stageId, levelId);
    if (hit) return sanitizeConfig({ ...hit.level.config, ...(overrides ?? {}) });
  }
  return sanitizeConfig({ ...getLevel(grade, level).config, ...(overrides ?? {}) });
}
